import React, { useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { BulkEditHeader, BulkEditFooter, VariablesSection } from './components'
import WarningAlert from '../ui/WarningAlert'
import { ApiClient } from 'services'
import { pluralize } from '../../utils/textHelpers'

const BulkEditSidebar = ({
  ensembleId,
  variables,
  selectedResourceIds,
  allResourcesSelected,
  searchQuery,
  totalResourceCount,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selectedVariables, setSelectedVariables] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  useEffect(() => {
    if (!isOpen) {
      setSelectedVariables([])
      setError(null)
      setFieldErrors({})
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      const { scrollY } = window

      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflowY = 'hidden'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''

      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
    }

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''
    }
  }, [isOpen])

  const resourceCount = useMemo(() => {
    const count = allResourcesSelected ? totalResourceCount : selectedResourceIds.length
    return pluralize(count, 'ressource')
  }, [allResourcesSelected, selectedResourceIds, totalResourceCount])

  const isFormValid = useMemo(() => {
    if (selectedVariables.length === 0)
      return false

    const fileTypes = ['pdf', 'image']
    return selectedVariables.every(item => {
      if (fileTypes.includes(item.variableType))
        return item.file !== null || item.clearFile
      return item.value !== '' || item.clearValue
    })
  }, [selectedVariables])

  const handleAddVariable = useCallback(variable => {
    setSelectedVariables(prev => [
      ...prev,
      {
        variableId: variable.id,
        variableName: variable.name,
        variableType: variable.variableType,
        value: '',
        file: null,
        fileName: '',
        clearFile: false,
        clearValue: false
      }
    ])
  }, [])

  const handleRemoveVariable = useCallback(variableId => {
    setSelectedVariables(prev => prev.filter(item => item.variableId !== variableId))
  }, [])

  const handleUpdateVariable = useCallback((variableId, field, value) => {
    setSelectedVariables(prev => prev.map(item => {
      if (item.variableId === variableId)
        return { ...item, [field]: value }
      return item
    }))
  }, [])

  const getVariableErrors = useCallback(variableId => {
    const key = `variables[${variableId}]`
    return fieldErrors[key]?.file || null
  }, [fieldErrors])

  const buildFormData = useCallback(() => {
    const formData = new FormData()

    if (allResourcesSelected)
      formData.append('all_resources', 'true')
    else
      formData.append('resource_ids', selectedResourceIds.join('-'))

    if (searchQuery)
      formData.append('q', searchQuery)

    selectedVariables.forEach((item, index) => {
      formData.append(`variables[${index}][variable_id]`, item.variableId)

      if (item.file)
        formData.append(`variables[${index}][file]`, item.file)
      else if (item.clearFile)
        formData.append(`variables[${index}][clear_file]`, 'true')
      else if (item.clearValue)
        formData.append(`variables[${index}][value]`, '')
      else
        formData.append(`variables[${index}][value]`, item.value)
    })

    return formData
  }, [selectedVariables, selectedResourceIds, allResourcesSelected, searchQuery])

  const handleSubmit = useCallback(() => {
    if (selectedVariables.length === 0)
      return

    setIsSubmitting(true)
    setError(null)

    const formData = buildFormData()

    ApiClient.postFormData(`/ensembles/${ensembleId}/bulk_edit_variables`, formData)
      .then(response => {
        setIsSubmitting(false)
        if (response.success === false) {
          setError(response.errors?.join(', ') || response.error || 'Une erreur est survenue')
          setFieldErrors(response.formatted_errors || {})
          return
        }
        onSuccess(response.notice)
      })
      .catch(err => {
        setIsSubmitting(false)
        setError(err.message || 'Une erreur est survenue')
      })
  }, [ensembleId, selectedVariables, buildFormData, onSuccess])

  if (!isOpen)
    return null

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />
      <div
        className="position-fixed h-100 bg-white"
        style={{
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: '620px',
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <BulkEditHeader onClose={onClose} />
        <div className="flex-grow-1 overflow-auto">
          <div className="px-3 py-4">
            <span className="d-block mb-3 text-success font-weight-bolder">
              {resourceCount}
              {' '}
              sélectionnés
            </span>
            <WarningAlert className="mb-3">
              <strong>Attention :</strong>
              {' '}
              Modifier ou supprimer des valeurs peut avoir des conséquences
              sur toutes les collectes où ces ressources apparaissent.
            </WarningAlert>
            {Boolean(error) && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="fas fa-exclamation-triangle mr-2" />
                {error}
              </div>
            )}
            <VariablesSection
              getVariableErrors={getVariableErrors}
              selectedVariables={selectedVariables}
              variables={variables}
              onAddVariable={handleAddVariable}
              onRemoveVariable={handleRemoveVariable}
              onUpdateVariable={handleUpdateVariable}
            />
          </div>
        </div>
        <BulkEditFooter
          disabled={!isFormValid}
          isSubmitting={isSubmitting}
          resourceCount={resourceCount}
          onApply={handleSubmit}
          onCancel={onClose}
        />
      </div>
    </>
  )
}

BulkEditSidebar.propTypes = {
  allResourcesSelected: PropTypes.bool,
  ensembleId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  selectedResourceIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  totalResourceCount: PropTypes.number,
  variables: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    variableType: PropTypes.string.isRequired
  })).isRequired
}

BulkEditSidebar.defaultProps = {
  allResourcesSelected: false,
  searchQuery: '',
  totalResourceCount: 0
}

export default BulkEditSidebar
